import { computed, type Ref } from 'vue';
import { useRoute, type RouteLocationNormalizedLoaded } from 'vue-router';
import type { SearchParams, SearchSort, SearchContext, SearchFilter } from '../types/search.types';

interface SearchProps {
  search?: string;
  searchSort?: SearchSort;
  filter?: SearchFilter;
  page?: number;
  amount?: number;
  searchUrl: string;
  context?: SearchContext;
  searchType?: 'name' | 'prefix';
}

function getSortFromPropsOrRoute(
  props: SearchProps,
  route: RouteLocationNormalizedLoaded
): SearchSort {
  const sortFromProps = props.searchSort?.sort;
  const orderFromProps = props.searchSort?.order;
  const sortFromRoute = (route.query.sort as string) || undefined;
  const orderFromRoute = (route.query.order as 'asc' | 'desc') || undefined;

  return {
    sort: sortFromProps ?? sortFromRoute,
    order: orderFromProps ?? orderFromRoute,
  };
}

function resolveSearchType(
  props: SearchProps,
  route: RouteLocationNormalizedLoaded,
  searchTypeRef?: Ref<'name' | 'prefix'>
): 'name' | 'prefix' {
  if (props.searchType) {
    return props.searchType;
  }

  const searchTypeFromRoute = route.query.searchType as 'name' | 'prefix' | undefined;
  if (searchTypeFromRoute) {
    return searchTypeFromRoute;
  }

  if (searchTypeRef?.value) {
    return searchTypeRef.value;
  }

  return 'name';
}

function buildFilterFromRoute(
  props: SearchProps,
  route: RouteLocationNormalizedLoaded
): SearchFilter {
  const excludedKeys = ['term', 'sort', 'order', 'searchType', 'page', 'amount'];
  const routeQueryEntries = Object.entries(route.query);

  const filterEntries = routeQueryEntries
    .filter(([key]) => !excludedKeys.includes(key))
    .map(([key, value]) => {
      const singleValue = Array.isArray(value) ? value[0] : value;
      return [key, singleValue];
    });

  const filterFromRoute = Object.fromEntries(filterEntries);

  return {
    ...props.filter,
    ...filterFromRoute,
  };
}

function buildSearchParams(
  props: SearchProps,
  route: RouteLocationNormalizedLoaded,
  searchTypeRef?: Ref<'name' | 'prefix'>,
  defaultContext?: SearchContext
): SearchParams {
  const search = props.search ?? (route.query.term as string) ?? '';

  const sort = getSortFromPropsOrRoute(props, route);

  const searchType = resolveSearchType(props, route, searchTypeRef);

  const filter = buildFilterFromRoute(props, route);

  const page = props.page ?? (Number(route.query.page) || 1);
  const amount = props.amount ?? (Number(route.query.amount) || 10);

  return {
    search,
    searchType,
    sort,
    filter,
    page,
    amount,
    searchUrl: props.searchUrl,
    context: props.context || defaultContext,
  };
}

export default function useSearchParams(
  props: SearchProps,
  defaultContext?: SearchContext,
  searchTypeRef?: Ref<'name' | 'prefix'>
): { searchParams: Ref<SearchParams> } {
  const route = useRoute();

  const searchParams = computed<SearchParams>(() =>
    buildSearchParams(props, route, searchTypeRef, defaultContext)
  );

  return {
    searchParams,
  };
}
